import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClipboardList, Loader2, Lock, Plus, X } from 'lucide-react';
import type { ServiceResourceRequirementInput } from '@/hooks/useSchedulingResources';
import type { FormsTemplateSummary } from '@/hooks/useFormsTemplates';
import type { QuestionnaireAutomationDraft } from '@/lib/preVisitQuestionnaireTiming';
import { QuestionnairePhaseList } from './QuestionnaireAutomationFields';
import { QuestionnaireMultiPicker } from '@/components/questionnaires/QuestionnaireMultiPicker';
import { filterAllowedQuestionnaires } from '@/lib/courseUiHelpers';
import { SERVICE_DURATION_PRESETS } from '@/lib/serviceDuration';
import {
  questionnairePickerName,
  toQuestionnairePickerItems,
} from '@/lib/questionnairePickerLabel';
import { Button } from '@/components/ui/Button';
import { FIELD_CONTROL } from '@/components/ui/fieldClasses';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { Toggle } from '@/components/ui/Toggle';
import { cn } from '@/utils/cn';
import { FORM_HINT_TYPE, FORM_LABEL_TYPE, TYPE } from '@/lib/typography';
import type {
  IntervalUnit,
  TreatmentCourseTemplateDraft,
  TreatmentCourseTemplateStepDraft,
} from '@/types/treatmentCourses';

export const DURATION_OPTIONS = SERVICE_DURATION_PRESETS;

export { FIELD_CONTROL };

export const RADIO_INPUT = 'h-4 w-4 border-ds-field text-ds-teal focus:shadow-ring';

export function RadioOption({
  name,
  checked,
  onChange,
  label,
}: {
  name: string;
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <label className="flex items-center gap-2 text-sm text-ds-text">
      <input
        type="radio"
        name={name}
        checked={checked}
        onChange={onChange}
        className={RADIO_INPUT}
      />
      {label}
    </label>
  );
}

export function IntervalFields({
  idPrefix,
  label,
  value,
  unit,
  onValueChange,
  onUnitChange,
}: {
  idPrefix: string;
  label: string;
  value: number;
  unit: IntervalUnit;
  onValueChange: (value: number) => void;
  onUnitChange: (unit: IntervalUnit) => void;
}) {
  const { t } = useTranslation();
  return (
    <div>
      <label htmlFor={`${idPrefix}-value`} className={cn('block', FORM_LABEL_TYPE)}>
        {label}
      </label>
      <div className="mt-1 flex gap-2">
        <input
          id={`${idPrefix}-value`}
          type="number"
          min={0}
          max={5200}
          value={value}
          onChange={(e) => onValueChange(Math.max(0, Math.min(5200, Number(e.target.value) || 0)))}
          className={cn(FIELD_CONTROL, 'w-24')}
        />
        <Select
          id={`${idPrefix}-unit`}
          value={unit}
          onChange={(e) => onUnitChange(e.target.value as IntervalUnit)}
          aria-label={label}
        >
          <option value="days">{t('treatment_courses.unit_days')}</option>
          <option value="weeks">{t('treatment_courses.unit_weeks')}</option>
        </Select>
      </div>
    </div>
  );
}

export function TreatmentCourseGeneralFields({
  draft,
  onChange,
  disabled,
}: {
  draft: TreatmentCourseTemplateDraft;
  onChange: (next: TreatmentCourseTemplateDraft) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <fieldset disabled={disabled} className="space-y-4">
      <Input
        name="treatment-course-name"
        label={t('treatment_courses.field_name')}
        value={draft.name}
        onChange={(e) => onChange({ ...draft, name: e.target.value })}
        required
      />
      <Textarea
        id="treatment-course-description"
        name="treatment-course-description"
        label={t('treatment_courses.field_description')}
        value={draft.description}
        onChange={(e) => onChange({ ...draft, description: e.target.value })}
        rows={2}
        placeholder={t('treatment_courses.field_description_placeholder')}
      />
      <div className="max-w-[12rem]">
        <label htmlFor="treatment-course-color" className={cn('block', FORM_LABEL_TYPE)}>
          {t('treatment_courses.field_color')}
        </label>
        <input
          id="treatment-course-color"
          type="color"
          value={draft.color}
          onChange={(e) => onChange({ ...draft, color: e.target.value })}
          className="mt-1 h-[42px] w-full cursor-pointer rounded-md border border-ds-field bg-ds-card p-1"
        />
      </div>

      <fieldset>
        <legend className={FORM_LABEL_TYPE}>{t('treatment_courses.field_booking_timing')}</legend>
        <div className="mt-2 space-y-2">
          <RadioOption
            name="booking-timing"
            checked={draft.booking_timing === 'all_upfront'}
            onChange={() => onChange({ ...draft, booking_timing: 'all_upfront' })}
            label={t('treatment_courses.booking_timing_all_upfront')}
          />
          <RadioOption
            name="booking-timing"
            checked={draft.booking_timing === 'allow_unbooked'}
            onChange={() => onChange({ ...draft, booking_timing: 'allow_unbooked' })}
            label={t('treatment_courses.booking_timing_allow_unbooked')}
          />
        </div>
      </fieldset>

      <Toggle
        checked={draft.remaining_steps_bookable_by === 'staff_and_patient'}
        onCheckedChange={(checked) =>
          onChange({
            ...draft,
            remaining_steps_bookable_by: checked ? 'staff_and_patient' : 'staff_only',
          })
        }
        label={t('treatment_courses.patient_can_book_remaining')}
      />

      <fieldset>
        <legend className={FORM_LABEL_TYPE}>{t('treatment_courses.field_billing_mode')}</legend>
        <div className="mt-2 space-y-2">
          <RadioOption
            name="billing-mode"
            checked={draft.billing_mode === 'per_step'}
            onChange={() =>
              onChange({ ...draft, billing_mode: 'per_step', package_price_cents: null })
            }
            label={t('treatment_courses.billing_per_step')}
          />
          <RadioOption
            name="billing-mode"
            checked={draft.billing_mode === 'package'}
            onChange={() =>
              onChange({
                ...draft,
                billing_mode: 'package',
                package_price_cents: draft.package_price_cents ?? 0,
              })
            }
            label={t('treatment_courses.billing_package')}
          />
        </div>
      </fieldset>

      {draft.billing_mode === 'package' ? (
        <Input
          name="treatment-course-package-price"
          label={t('treatment_courses.field_package_price')}
          type="number"
          min={0}
          step={1}
          value={((draft.package_price_cents ?? 0) / 100).toString()}
          onChange={(e) =>
            onChange({
              ...draft,
              package_price_cents: Math.max(0, Math.round(Number(e.target.value) * 100)),
            })
          }
        />
      ) : null}
    </fieldset>
  );
}

export function TreatmentCourseAppointmentFields({
  index,
  step,
  services,
  onChange,
  disabled,
}: {
  index: number;
  step: TreatmentCourseTemplateStepDraft;
  services: Array<{ id: string; name: string; duration_minutes: number; is_active: boolean }>;
  onChange: (next: TreatmentCourseTemplateStepDraft) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const selectedService = services.find((svc) => svc.id === step.service_id);
  const durationOptions = useMemo(() => {
    const values = new Set(DURATION_OPTIONS);
    if (step.duration_minutes != null) values.add(step.duration_minutes);
    if (selectedService) values.add(selectedService.duration_minutes);
    return [...values].sort((a, b) => a - b);
  }, [selectedService, step.duration_minutes]);

  return (
    <fieldset disabled={disabled} className="space-y-4">
      <div>
        <label
          htmlFor={`treatment-course-step-service-${step.clientId}`}
          className={cn('block', FORM_LABEL_TYPE)}
        >
          {t('treatment_courses.field_service')}
        </label>
        <Select
          id={`treatment-course-step-service-${step.clientId}`}
          value={step.service_id}
          onChange={(e) => onChange({ ...step, service_id: e.target.value })}
          className="mt-1"
          required
        >
          <option value="">{t('treatment_courses.service_placeholder')}</option>
          {services
            .filter((svc) => svc.is_active || svc.id === step.service_id)
            .map((svc) => (
              <option key={svc.id} value={svc.id}>
                {svc.name}
              </option>
            ))}
        </Select>
      </div>

      <Input
        name={`treatment-course-step-title-${step.clientId}`}
        label={t('treatment_courses.field_step_title')}
        value={step.title}
        onChange={(e) => onChange({ ...step, title: e.target.value })}
        hint={t('treatment_courses.field_step_title_hint')}
      />

      <div>
        <label
          htmlFor={`treatment-course-step-duration-${step.clientId}`}
          className={cn('block', FORM_LABEL_TYPE)}
        >
          {t('treatment_courses.field_duration')}
        </label>
        <Select
          id={`treatment-course-step-duration-${step.clientId}`}
          value={step.duration_minutes ?? ''}
          onChange={(e) =>
            onChange({
              ...step,
              duration_minutes: e.target.value === '' ? null : Number(e.target.value),
            })
          }
          className="mt-1"
        >
          <option value="">
            {selectedService
              ? t('treatment_courses.duration_inherit', {
                  minutes: selectedService.duration_minutes,
                })
              : t('treatment_courses.duration_inherit_generic')}
          </option>
          {durationOptions.map((minutes) => (
            <option key={minutes} value={minutes}>
              {minutes} {t('services.minutes_short')}
            </option>
          ))}
        </Select>
      </div>

      {index > 0 ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <IntervalFields
            idPrefix={`${step.clientId}-interval`}
            label={t('treatment_courses.field_interval')}
            value={step.interval_value}
            unit={step.interval_unit}
            onValueChange={(interval_value) => onChange({ ...step, interval_value })}
            onUnitChange={(interval_unit) => onChange({ ...step, interval_unit })}
          />
          <IntervalFields
            idPrefix={`${step.clientId}-tolerance`}
            label={t('treatment_courses.field_tolerance')}
            value={step.interval_tolerance_value}
            unit={step.interval_tolerance_unit}
            onValueChange={(interval_tolerance_value) =>
              onChange({ ...step, interval_tolerance_value })
            }
            onUnitChange={(interval_tolerance_unit) =>
              onChange({ ...step, interval_tolerance_unit })
            }
          />
        </div>
      ) : (
        <p className={FORM_HINT_TYPE}>{t('treatment_courses.interval_first_step_hint')}</p>
      )}
    </fieldset>
  );
}

export function TreatmentCoursePractitionerFields({
  step,
  members,
  onChange,
  disabled,
}: {
  step: TreatmentCourseTemplateStepDraft;
  members: Array<{ user_id: string; name: string }>;
  onChange: (next: TreatmentCourseTemplateStepDraft) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <fieldset disabled={disabled} className="space-y-3">
      <legend className={FORM_LABEL_TYPE}>{t('treatment_courses.field_practitioners')}</legend>
      <div className="space-y-2">
        <RadioOption
          name={`practitioner-mode-${step.clientId}`}
          checked={step.practitioner_mode === 'inherit'}
          onChange={() => onChange({ ...step, practitioner_mode: 'inherit' })}
          label={t('treatment_courses.practitioners_inherit')}
        />
        <RadioOption
          name={`practitioner-mode-${step.clientId}`}
          checked={step.practitioner_mode === 'all'}
          onChange={() => onChange({ ...step, practitioner_mode: 'all' })}
          label={t('treatment_courses.practitioners_all')}
        />
        <RadioOption
          name={`practitioner-mode-${step.clientId}`}
          checked={step.practitioner_mode === 'selected'}
          onChange={() => onChange({ ...step, practitioner_mode: 'selected' })}
          label={t('treatment_courses.practitioners_selected')}
        />
      </div>
      {step.practitioner_mode === 'selected' ? (
        <PractitionerChecklist
          members={members}
          selectedIds={step.practitioner_ids}
          onChange={(practitioner_ids) => onChange({ ...step, practitioner_ids })}
        />
      ) : null}
    </fieldset>
  );
}

export function TreatmentCourseResourceFields({
  step,
  resourceGroups,
  resources,
  onChange,
  disabled,
}: {
  step: TreatmentCourseTemplateStepDraft;
  resourceGroups: Array<{ id: string; name: string; is_active: boolean }>;
  resources: Array<{
    id: string;
    name: string;
    resource_group_id: string;
    is_active: boolean;
  }>;
  onChange: (next: TreatmentCourseTemplateStepDraft) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <fieldset disabled={disabled} className="space-y-3">
      <legend className={FORM_LABEL_TYPE}>{t('treatment_courses.field_resources')}</legend>
      <div className="space-y-2">
        <RadioOption
          name={`resource-mode-${step.clientId}`}
          checked={step.resource_mode === 'inherit'}
          onChange={() => onChange({ ...step, resource_mode: 'inherit' })}
          label={t('treatment_courses.resources_inherit')}
        />
        <RadioOption
          name={`resource-mode-${step.clientId}`}
          checked={step.resource_mode === 'override'}
          onChange={() => onChange({ ...step, resource_mode: 'override' })}
          label={t('treatment_courses.resources_override')}
        />
      </div>
      {step.resource_mode === 'override' ? (
        <ResourceRequirementsEditor
          requirements={step.resource_requirements}
          resourceGroups={resourceGroups}
          resources={resources}
          onChange={(resource_requirements) => onChange({ ...step, resource_requirements })}
        />
      ) : null}
    </fieldset>
  );
}

export function TreatmentCourseQuestionnaireFields({
  step,
  stepIndex,
  stepTitle,
  templates,
  templatesLoading,
  onChange,
  disabled,
}: {
  step: TreatmentCourseTemplateStepDraft;
  stepIndex: number;
  stepTitle: string;
  templates: FormsTemplateSummary[];
  templatesLoading: boolean;
  onChange: (next: TreatmentCourseTemplateStepDraft) => void;
  disabled?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const [pickerOpen, setPickerOpen] = useState(false);
  const allowed = step.allowed_forms_template_ids;
  const autoTemplates =
    allowed.length > 0 ? filterAllowedQuestionnaires(templates, allowed) : templates;
  const pickerItems = toQuestionnairePickerItems(
    templates,
    i18n.language,
    t('treatment_courses.q_unnamed'),
  );

  return (
    <div className="sk" data-testid="treatment-course-allowed-questionnaires">
      <section className="qs" aria-labelledby="h-allow">
        <div className="qs-h">
          <h3 id="h-allow">{t('treatment_courses.allowed_questionnaires')}</h3>
          {allowed.length > 0 ? <span className="tcount num">{allowed.length}</span> : null}
        </div>
        <p className="hint">{t('treatment_courses.allowed_questionnaires_hint')}</p>
        {allowed.length === 0 ? (
          <div className="q-free">
            <ClipboardList className="i sm" aria-hidden />
            <div className="grow">
              <b>{t('treatment_courses.allowed_empty_title')}</b>
              <p className="num">
                {t('treatment_courses.allowed_empty_hint', { count: templates.length })}
              </p>
              {disabled ? null : (
                <div className="qadd" style={{ marginTop: 0 }}>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    aria-haspopup="dialog"
                    aria-expanded={pickerOpen}
                    onClick={() => setPickerOpen(true)}
                  >
                    <Plus className="i sm" aria-hidden />
                    {t('treatment_courses.add_questionnaire')}
                  </Button>
                  <QuestionnaireMultiPicker
                    open={pickerOpen}
                    title={t('treatment_courses.allowed_questionnaires')}
                    subtitle={t('treatment_courses.step_n_named', {
                      n: stepIndex + 1,
                      name: stepTitle,
                    })}
                    items={pickerItems}
                    lockedIds={allowed}
                    onClose={() => setPickerOpen(false)}
                    onConfirm={(ids) =>
                      onChange({
                        ...step,
                        allowed_forms_template_ids: [...new Set([...allowed, ...ids])],
                      })
                    }
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="qchips" role="list" aria-label={t('treatment_courses.allowed_questionnaires')}>
              {allowed.map((id) => {
                const template = templates.find((item) => item.id === id);
                const name = template
                  ? questionnairePickerName(template, i18n.language, t('treatment_courses.q_unnamed'))
                  : t('treatment_courses.q_unnamed');
                return (
                  <span key={id} className="qchip" role="listitem">
                    {name}
                    {disabled ? null : (
                      <button
                        type="button"
                        className="x"
                        aria-label={t('treatment_courses.allowed_remove', { name })}
                        onClick={() =>
                          onChange({
                            ...step,
                            allowed_forms_template_ids: allowed.filter((value) => value !== id),
                          })
                        }
                      >
                        <X className="i sm" />
                      </button>
                    )}
                  </span>
                );
              })}
            </div>
            {disabled ? null : (
              <div className="qadd">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  aria-haspopup="dialog"
                  aria-expanded={pickerOpen}
                  onClick={() => setPickerOpen(true)}
                >
                  <Plus className="i sm" aria-hidden />
                  {t('treatment_courses.add_questionnaire')}
                </Button>
                <QuestionnaireMultiPicker
                  open={pickerOpen}
                  title={t('treatment_courses.allowed_questionnaires')}
                  subtitle={t('treatment_courses.step_n_named', {
                    n: stepIndex + 1,
                    name: stepTitle,
                  })}
                  items={pickerItems}
                  lockedIds={allowed}
                  onClose={() => setPickerOpen(false)}
                  onConfirm={(ids) =>
                    onChange({
                      ...step,
                      allowed_forms_template_ids: [...new Set([...allowed, ...ids])],
                    })
                  }
                />
              </div>
            )}
          </>
        )}
      </section>

      <section className="qs" aria-labelledby="h-auto">
        <div className="qs-h">
          <h3 id="h-auto">{t('treatment_courses.send_auto_title')}</h3>
        </div>
        <p className="hint">{t('treatment_courses.send_auto_hint')}</p>
        <fieldset disabled={disabled} className="radios space-y-2">
          <legend className="sr-only">{t('treatment_courses.send_auto_title')}</legend>
          <RadioOption
            name={`questionnaire-mode-${step.clientId}`}
            checked={step.questionnaire_mode === 'inherit'}
            onChange={() => onChange({ ...step, questionnaire_mode: 'inherit' })}
            label={t('treatment_courses.questionnaires_inherit')}
          />
          <RadioOption
            name={`questionnaire-mode-${step.clientId}`}
            checked={step.questionnaire_mode === 'inherit_plus'}
            onChange={() => onChange({ ...step, questionnaire_mode: 'inherit_plus' })}
            label={t('treatment_courses.questionnaires_inherit_plus')}
          />
          <RadioOption
            name={`questionnaire-mode-${step.clientId}`}
            checked={step.questionnaire_mode === 'replace'}
            onChange={() => onChange({ ...step, questionnaire_mode: 'replace' })}
            label={t('treatment_courses.questionnaires_replace')}
          />
        </fieldset>
        {step.questionnaire_mode !== 'inherit' ? (
          <StepQuestionnaires
            automations={step.automations}
            templates={autoTemplates}
            templatesLoading={templatesLoading}
            onChange={(automations) => onChange({ ...step, automations })}
          />
        ) : (
          <p className={cn('mt-3', FORM_HINT_TYPE)}>
            {t('treatment_courses.questionnaires_inherit_hint')}
          </p>
        )}
        {allowed.length > 0 && step.questionnaire_mode !== 'inherit' ? (
          <p className="only">
            <Lock className="i xs" aria-hidden />
            {t('treatment_courses.allowed_auto_only', { count: allowed.length })}
          </p>
        ) : null}
      </section>
    </div>
  );
}

function PractitionerChecklist({
  members,
  selectedIds,
  onChange,
}: {
  members: Array<{ user_id: string; name: string }>;
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      data-testid="treatment-course-practitioner-checklist"
      role="group"
      aria-label={t('treatment_courses.practitioners_selected')}
      className="mt-3 max-h-48 space-y-1 overflow-y-auto rounded-md border border-ds-line p-2"
    >
      {members.map((member) => {
        const checked = selectedIds.includes(member.user_id);
        return (
          <label key={member.user_id} className="flex items-center gap-2 text-sm text-ds-text">
            <input
              type="checkbox"
              checked={checked}
              onChange={() =>
                onChange(
                  checked
                    ? selectedIds.filter((id) => id !== member.user_id)
                    : [...selectedIds, member.user_id],
                )
              }
              className={RADIO_INPUT}
            />
            {member.name}
          </label>
        );
      })}
      {members.length === 0 ? (
        <p className={FORM_HINT_TYPE}>{t('services.practitioners_empty')}</p>
      ) : null}
    </div>
  );
}

function ResourceRequirementsEditor({
  requirements,
  resourceGroups,
  resources,
  onChange,
}: {
  requirements: ServiceResourceRequirementInput[];
  resourceGroups: Array<{ id: string; name: string; is_active: boolean }>;
  resources: Array<{
    id: string;
    name: string;
    resource_group_id: string;
    is_active: boolean;
  }>;
  onChange: (next: ServiceResourceRequirementInput[]) => void;
}) {
  const { t } = useTranslation();

  function addRequirement() {
    onChange([
      ...requirements,
      {
        resource_group_id: '',
        quantity: 1,
        eligibility_mode: 'any',
        eligible_resource_ids: [],
      },
    ]);
  }

  return (
    <div className="mt-3 space-y-3">
      <p className={cn('mb-1', FORM_HINT_TYPE)}>{t('services.resources_required_hint')}</p>
      {requirements.length === 0 ? (
        <div className="rounded-md border border-dashed border-ds-line px-3 py-4 text-sm text-ds-muted">
          <p>{t('services.resources_none')}</p>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="mt-3"
            onClick={addRequirement}
          >
            <Plus className="mr-1 h-4 w-4" />
            {t('services.add_resource_requirement')}
          </Button>
        </div>
      ) : (
        <>
          {requirements.map((req, index) => {
            const groupResources = resources.filter(
              (resource) => resource.resource_group_id === req.resource_group_id,
            );
            return (
              <div
                key={`${req.resource_group_id}-${index}`}
                className="space-y-2 rounded-md border border-ds-line p-3"
              >
                <div className="grid grid-cols-[1fr_5rem_auto] gap-2">
                  <Select
                    value={req.resource_group_id}
                    onChange={(e) => {
                      const next = [...requirements];
                      next[index] = {
                        ...req,
                        resource_group_id: e.target.value,
                        eligible_resource_ids: [],
                        eligibility_mode: 'any',
                      };
                      onChange(next);
                    }}
                  >
                    <option value="">{t('services.resource_group_placeholder')}</option>
                    {resourceGroups
                      .filter((group) => group.is_active || group.id === req.resource_group_id)
                      .map((group) => (
                        <option key={group.id} value={group.id}>
                          {group.name}
                        </option>
                      ))}
                  </Select>
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={req.quantity}
                    onChange={(e) => {
                      const next = [...requirements];
                      next[index] = {
                        ...req,
                        quantity: Math.max(1, Math.min(100, Number(e.target.value) || 1)),
                      };
                      onChange(next);
                    }}
                    className={cn(FIELD_CONTROL, 'px-2')}
                    aria-label={t('services.resource_quantity')}
                  />
                  <Button
                    type="button"
                    variant="tertiary"
                    size="icon"
                    onClick={() => onChange(requirements.filter((_, i) => i !== index))}
                    aria-label={t('common.delete')}
                  >
                    <X />
                  </Button>
                </div>
                {req.resource_group_id && groupResources.length > 0 ? (
                  <div>
                    <p className={cn('mb-1 font-medium text-ds-muted', TYPE.small)}>
                      {t('services.eligible_resources')}
                    </p>
                    <div className="space-y-1">
                      {groupResources.map((resource) => {
                        const selected =
                          req.eligibility_mode !== 'selected' ||
                          req.eligible_resource_ids.includes(resource.id);
                        return (
                          <label
                            key={resource.id}
                            className="flex items-center gap-2 text-sm text-ds-text"
                          >
                            <input
                              type="checkbox"
                              checked={req.eligibility_mode !== 'selected' ? true : selected}
                              onChange={() => {
                                const currentlySelected =
                                  req.eligibility_mode === 'selected'
                                    ? req.eligible_resource_ids
                                    : groupResources.map((item) => item.id);
                                const nextIds = currentlySelected.includes(resource.id)
                                  ? currentlySelected.filter((id) => id !== resource.id)
                                  : [...currentlySelected, resource.id];
                                const next = [...requirements];
                                const every = nextIds.length === groupResources.length;
                                next[index] = {
                                  ...req,
                                  eligibility_mode: every ? 'any' : 'selected',
                                  eligible_resource_ids: every ? [] : nextIds,
                                };
                                onChange(next);
                              }}
                              className={RADIO_INPUT}
                            />
                            {resource.name}
                            {!resource.is_active ? ` (${t('settings.resources.inactive')})` : ''}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
          <Button type="button" variant="secondary" size="sm" onClick={addRequirement}>
            <Plus className="mr-1 h-4 w-4" />
            {t('services.add_resource_requirement')}
          </Button>
        </>
      )}
    </div>
  );
}

function StepQuestionnaires({
  automations,
  templates,
  templatesLoading,
  onChange,
}: {
  automations: QuestionnaireAutomationDraft[];
  templates: FormsTemplateSummary[];
  templatesLoading: boolean;
  onChange: (next: QuestionnaireAutomationDraft[]) => void;
}) {
  const { t } = useTranslation();

  if (templatesLoading) {
    return (
      <div className="mt-3 flex items-center gap-1.5 text-xs text-ds-muted">
        <Loader2 className="h-3 w-3 animate-spin" />
        {t('common.loading', 'Loading…')}
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <p className={cn('mt-3', FORM_HINT_TYPE)}>{t('services.field_auto_send_no_templates')}</p>
    );
  }

  return (
    <div className="mt-4 space-y-6">
      <QuestionnairePhaseList
        phase="before_appointment"
        automations={automations}
        templates={templates}
        onChange={onChange}
        title={t('treatment_courses.questionnaires_before')}
      />
      <QuestionnairePhaseList
        phase="after_appointment"
        automations={automations}
        templates={templates}
        onChange={onChange}
        title={t('treatment_courses.questionnaires_after')}
      />
    </div>
  );
}
